import { FC, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import useUnsavedChangesBlocker from "@/shared/hooks/useUnsavedChangesBlocker";
import { AdRoutes } from "@/constants/ad-routes";
import useAddOrganizationForm from "../hooks/useAddOrganizationForm";
import AddOrganizationForm from "../components/AddOrganizationForm";

const AddOrganizationPage: FC = () => {
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  const {
    organizationForm,
    homeImageError,
    clearHomeImageError,
    isLoading,
    placePredictions,
    getPlacePredictions,
    getAddressComponents,
  } = useAddOrganizationForm((data) => {
    toast.success("Organization created successfully");
    setOrganizationId(data.data?.id ?? null);
  });

  useUnsavedChangesBlocker(organizationForm.dirty && !organizationId);

  useEffect(() => {
    if (organizationId) {
      navigate(
        AdRoutes.organizationDetail.replace(":organizationId", organizationId),
      );
    }
  }, [organizationId, navigate]);

  return (
    <DashboardContentLayout horizontalPadding="40px">
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Manage Organizations", url: AdRoutes.organizations },
              { label: "Add New Organization" },
            ]}
          />
          <HeaderItemsRight>
            <Button
              variant="contained"
              disabled={
                isLoading ||
                (organizationForm.values.showPromotionalBanner &&
                  Boolean(homeImageError)) ||
                !organizationForm.dirty ||
                !organizationForm.isValid
              }
              onClick={() => organizationForm.submitForm()}
              data-testid="save-organization"
            >
              Save Organization
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <AddOrganizationForm
        organizationForm={organizationForm}
        homeImageError={homeImageError}
        onHomeImageErrorDismiss={clearHomeImageError}
        placePredictions={placePredictions}
        getPlacePredictions={getPlacePredictions}
        getAddressComponents={getAddressComponents}
      />
    </DashboardContentLayout>
  );
};

export default AddOrganizationPage;
